import { get_cdn_url, tool_map, use_store } from "./store";
import { Error } from "./components/Error";
import { Message } from "./components/Message";
import Footer from "./components/footer";
import Header from "./components/header";
import FaqSection from "./components/faq";
import Analysis from "./components/analysis";
import AccountPage from "./components/account";
import AccountPageV2 from "./components/account_v2";
import SetToken from "./components/SetToken";
import WhySubscribeCard from "./components/why_subscribe_card";
import { Route, Switch, useLocation } from "wouter";
import React, { useEffect, useState } from "react";
import { LANGUAGE_CODES } from "./localize";
import ThankYouPage from "./components/thankyou";
import MarketingTable from "./components/marketing_table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRight } from "@fortawesome/pro-regular-svg-icons";
import { usePostHog } from "posthog-js/react";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { enUS, esES, frFR, ptBR, jaJP } from "@clerk/localizations";
import { ReviewSection } from "./components/review_section";
import { HowLuluWorks } from "./components/how_lulu_works";
import { HelmetProvider } from "react-helmet-async";
import StripePortal from "./components/stripe_portal";

// Language wrapper component
const LanguageWrapper = ({
  Component,
  params,
  ...otherProps
}: {
  Component: any;
  params?: any;
  [key: string]: any;
}) => {
  const { set_ln, ln } = use_store();

  useEffect(() => {
    if (params?.ln) {
      if (params?.ln !== ln) {
        set_ln(params.ln, false);
      }
    }
  }, [params?.ln, set_ln, ln]);

  return <Component params={params} {...otherProps} />;
};

const ToolList = () => {
  const { t_url } = use_store();

  return (
    <div className="flex flex-col gap-[12px]">
      {tool_map.map((x, index) => {
        return (
          <a
            href={t_url(x.href)}
            className="p-[16px] bg-surface border-lines border-[1px] rounded-[4px] block group"
            key={index}
          >
            <div className="flex flex-row w-full justify-between items-center">
              <div className="flex-row flex items-center justify-start gap-[8px] mb-[16px]">
                <img
                  src={get_cdn_url(`/brands/${x.tool}.webp`)}
                  className="mb-[4px]"
                  loading="lazy"
                  style={{
                    width: x.tool === "dating_tracker" ? 38 : 32,
                    height: 32,
                  }}
                ></img>
              </div>
              <div className="bigt mb-[6px] text-primary group-hover:text-brand-2">
                <FontAwesomeIcon
                  className="text-[18px]"
                  icon={faArrowUpRight}
                ></FontAwesomeIcon>
              </div>
            </div>
            <p className="bigt mb-[4px]">{x.name}</p>
            <p className="smallt">{x.hero}</p>
          </a>
        );
      })}
    </div>
  );
};

const AllToolsView = () => {
  const { set_tool, tool, t } = use_store();
  useEffect(() => {
    set_tool(undefined);
  }, [tool]);
  return (
    <>
      <div className="eyebrows w-full flex flex-row justify-start sm:justify-center items-center gap-[8px]">
        <div className="">{t("TOOLS BY LULU LOYAL")}</div>
      </div>
      <div className="flex flex-row items-center justify-center w-full mt-[8px]  mb-[32px]">
        <p className="mtitle sm:text-center">
          {t("Tools to keep tabs without saying a word.")}
          <span className="text-brand-2 mtitle">
            {" "}
            {t("100% anonymous and untraceable.")}
          </span>
        </p>
      </div>
      <ToolList></ToolList>
      <div className="w-full h-[1px] bg-lines my-[64px]"></div>
      <ReviewSection></ReviewSection>
    </>
  );
};

// Home page component
const HomePage = ({
  showWhySubscribe = false,
  params,
}: {
  showWhySubscribe?: boolean;
  params?: any;
}) => {
  const { set_tool, set_account_analysis } = use_store();

  useEffect(() => {
    // Extract mode and platform from URL params if provided
    if (params?.mode && params?.platform) {
      const mode = params.mode as
        | "followers"
        | "stories"
        | "dating"
        | "redflag"
        | undefined;
      const platform = params.platform as
        | "instagram"
        | "tiktok"
        | "snapchat"
        | "tracker"
        | "reddit"
        | undefined;
      set_account_analysis(undefined);
      // Map platform and mode to tool
      if (platform === "instagram" && mode === "followers") {
        set_tool("instagram_loyalty");
      } else if (platform === "instagram" && mode === "stories") {
        set_tool("instagram_stories");
      } else if (platform === "tiktok" && mode === "followers") {
        set_tool("tiktok_loyalty");
      } else if (platform === "snapchat" && mode === "stories") {
        set_tool("snapchat_stories");
      } else if (platform === "tracker" && mode === "dating") {
        set_tool("dating_tracker");
      } else if (platform === "reddit" && mode === "redflag") {
        set_tool("reddit_red_flags");
      } else {
        set_tool(undefined);
      }
    } else {
      set_tool(undefined);
    }
  }, [params?.mode, params?.platform, set_tool]);

  return (
    <>
      {showWhySubscribe ? <WhySubscribeCard /> : <Analysis />}
      {(!showWhySubscribe) && (
        <>
          <div className="w-full h-[1px] bg-lines my-[64px]"></div>
          <HowLuluWorks></HowLuluWorks>
        </>
      )}

      <div className="w-full h-[1px] bg-lines my-[64px]"></div>
      <MarketingTable></MarketingTable>

      <div className="w-full h-[1px] bg-lines my-[64px]"></div>
      <ReviewSection></ReviewSection>

      <div className="w-full h-[1px] bg-lines my-[64px]"></div>
      <FaqSection />

      <div className="w-full h-[1px] bg-lines my-[64px]"></div>
      <ToolList></ToolList>
    </>
  );
};

// Account wrapper component
const AccountWrapper = ({
  account_mode,
}: {
  account_mode: "create" | "login";
}) => {
  const { user } = use_store();

  if (user && user.breaking_version === "v2") {
    return <AccountPageV2 account_mode={account_mode} />;
  } else {
    return <AccountPage account_mode={account_mode} />;
  }
};

// Clerk handler component
const ClerkHandler = () => {
  const { set_clerk_token, clerk_token } = use_store();
  const { getToken } = useAuth();

  useEffect(() => {
    const interval = setInterval(async () => {
      const new_clerk_token = await getToken();
      if (new_clerk_token !== null && clerk_token != new_clerk_token) {
        set_clerk_token(new_clerk_token);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [getToken, set_clerk_token]);

  return null;
};

const CLERK_KEY =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    ? "pk_test_bGl2aW5nLWRpbm9zYXVyLTc1LmNsZXJrLmFjY291bnRzLmRldiQ"
    : "pk_live_Y2xlcmsubHVsdWxveWFsLmNvbSQ";

export const App = () => {
  // @ts-ignore
  const {
    error,
    message,
    refresh_current_user,
    token,
    set_ln,
    ln,
    clerk_token,
    refresh_unique_data,
    session_unique_id,
    update_flags,
    tool,
    set_tool,
    user,
  } = use_store();
  const [location, set_location] = useLocation();
  const posthog = usePostHog();
  const [clerk_ln, set_clerk_ln] = useState(enUS);
  const [consumable_params, set_consumable_params] = useState({});

  useEffect(() => {
    // Set Clerk localization based on language
    switch (ln) {
      case "es":
        set_clerk_ln(esES);
        break;
      case "fr":
        set_clerk_ln(frFR);
        break;
      case "pt":
        set_clerk_ln(ptBR);
        break;
      case "ja":
        set_clerk_ln(jaJP);
        break;
      case "en":
      default:
        set_clerk_ln(enUS);
        break;
    }
  }, [ln]);

  useEffect(() => {
    if (!session_unique_id) {
      return;
    } else {
      document.cookie = `unique_id=${session_unique_id}; path=/; max-age=${60 * 60 * 24 * 365 * 10}; SameSite=Lax`;
    }
    const flag_params = ["fabdary", "fbclid", "ttclid"];
    const url_params = new URLSearchParams(window.location.search);
    const flags_to_update: Record<string, string> = {};

    for (const flag of flag_params) {
      const value = url_params.get(flag);
      if (value !== null) {
        flags_to_update[flag] = value;
      }
    }

    if (Object.keys(flags_to_update).length > 0) {
      const new_params = { ...consumable_params, ...flags_to_update };
      set_consumable_params(new_params);
      update_flags(new_params);
      // Clear all URL params
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [consumable_params, set_consumable_params, session_unique_id]);

  useEffect(() => {
    let currentPath = location;
    const languagePattern = /^\/([a-z]{2}|z0)(\/|$)/;
    const match = currentPath.match(languagePattern);

    if (match) {
      currentPath = currentPath.slice(match[1].length + 1) || "/";
    }

    const newPath = `/${ln}${currentPath}`;
    if (currentPath.length > 2 && newPath !== location) {
      set_location(newPath);
      return;
    }

    // Only redirect if we're truly at a root path without the mode/platform
    const isRootPath =
      location === "" ||
      location === "/" ||
      location === `/${ln}` ||
      location === `/${ln}/` ||
      location === `${ln}`;

    let target_path = ln ? `/${ln}/alltools` : `/alltools`;
    if (tool != undefined) {
      target_path = ln
        ? `/${ln}${tool_map.filter((x) => x.tool === tool)[0].href}`
        : `${tool_map.filter((x) => x.tool === tool)[0].href}`;
    }

    if (isRootPath && location !== target_path) {
      set_location(target_path);
      return;
    }

    if (Object.keys(consumable_params).includes("fabdary")) {
      let np = consumable_params;
      // @ts-ignore
      delete np.fabdary;
      set_consumable_params(np);
      set_location(ln ? `/${ln}/followers/instagram` : `/followers/instagram`);
    }
  }, [
    location,
    ln,
    set_location,
    tool,
    set_tool,
    consumable_params,
    set_consumable_params,
  ]);

  useEffect(() => {
    const old_distinct_id = posthog.get_distinct_id();
    if (old_distinct_id != session_unique_id) {
      posthog.alias(session_unique_id);
      posthog.identify(session_unique_id);
    }
    if (user && user.unique_id) {
      document.cookie = `unique_id=${user.unique_id}; path=/; max-age=${60 * 60 * 24 * 365 * 10}; SameSite=Lax`;
    } else {
      document.cookie = `unique_id=${session_unique_id}; path=/; max-age=${60 * 60 * 24 * 365 * 10}; SameSite=Lax`;
    }

    refresh_unique_data();
  }, [session_unique_id, user]);

  useEffect(() => {
    // Set initial language based on navigator.language
    const updateLanguage = () => {
      const browserLang = String(navigator.language).toLowerCase();
      let targetLang: string;

      // First check for exact language-region matches (e.g., pt-BR, en-US)
      if (browserLang.startsWith("pt-")) {
        targetLang = "pt";
      } else if (browserLang.startsWith("es-")) {
        targetLang = "es";
      } else if (browserLang.startsWith("fr-")) {
        targetLang = "fr";
      } else if (browserLang.startsWith("ja-")) {
        targetLang = "ja";
      } else if (browserLang.startsWith("en-")) {
        targetLang = "en";
      }
      // Then check for language codes without region (e.g., pt, es, fr)
      else if (browserLang.startsWith("pt")) {
        targetLang = "pt";
      } else if (browserLang.startsWith("es")) {
        targetLang = "es";
      } else if (browserLang.startsWith("fr")) {
        targetLang = "fr";
      } else if (browserLang.startsWith("ja")) {
        targetLang = "ja";
      } else {
        targetLang = "en";
      }

      // Only set if different from current language
      if (targetLang !== ln) {
        set_ln(targetLang);
      }
    };

    // Set initial language
    updateLanguage();

    // Listen for language changes
    window.addEventListener("languagechange", updateLanguage);

    return () => {
      window.removeEventListener("languagechange", updateLanguage);
    };
  }, [ln]);

  useEffect(() => {
    if (token || clerk_token) {
      refresh_current_user();
      refresh_unique_data();
    }
  }, [token, clerk_token]);

  return (
    <HelmetProvider>
      <ClerkProvider publishableKey={CLERK_KEY} localization={clerk_ln}>
        <ClerkHandler />
        <div className="min-h-screen bg-surface-secondary flex flex-col items-center max-w-screen">
        <Header />
        <div className="w-full px-[16px] bigt py-[32px] sm:py-[48px] flex flex-col items-center max-w-[640px]">
          <Switch>
            {/* Default routes without language prefix */}
            <Route path="/" component={HomePage} />
            <Route path="/try">
              <HomePage showWhySubscribe={true} />
            </Route>
            <Route path="/alltools">
              <AllToolsView />
            </Route>
            <Route path="/thankyou" component={ThankYouPage} />
            <Route path="/set_token" component={SetToken} />
            <Route path="/account">
              {() => <AccountWrapper account_mode="create" />}
            </Route>
            <Route path="/login">
              {() => <AccountWrapper account_mode="login" />}
            </Route>
            <Route path="/pay" component={StripePortal} />

            {/* Routes with language prefix */}
            {LANGUAGE_CODES.map((lang_code) => (
              <React.Fragment key={lang_code}>
                <Route key={`${lang_code}-try`} path={`/${lang_code}/try`}>
                  {(params) => (
                    <LanguageWrapper
                      Component={HomePage}
                      showWhySubscribe={true}
                      params={{ ...params, ln: lang_code }}
                    />
                  )}
                </Route>

                <Route
                  key={`${lang_code}-alltools`}
                  path={`/${lang_code}/alltools`}
                >
                  {(params) => (
                    <LanguageWrapper
                      Component={AllToolsView}
                      params={{ ...params, ln: lang_code }}
                    />
                  )}
                </Route>

                <Route
                  key={`${lang_code}-account`}
                  path={`/${lang_code}/account`}
                >
                  {() => {
                    return (
                      <LanguageWrapper
                        Component={AccountWrapper}
                        account_mode="create"
                        params={{ ln: lang_code }}
                      />
                    );
                  }}
                </Route>
                <Route key={`${lang_code}-login`} path={`/${lang_code}/login`}>
                  {() => {
                    return (
                      <LanguageWrapper
                        Component={AccountWrapper}
                        account_mode="login"
                        params={{ ln: lang_code }}
                      />
                    );
                  }}
                </Route>
                <Route
                  key={`${lang_code}-thankyou`}
                  path={`/${lang_code}/thankyou`}
                >
                  {() => (
                    <LanguageWrapper
                      Component={ThankYouPage}
                      params={{ ln: lang_code }}
                    />
                  )}
                </Route>
                <Route
                  key={`${lang_code}-set-token`}
                  path={`/${lang_code}/set_token`}
                >
                  {() => (
                    <LanguageWrapper
                      Component={SetToken}
                      params={{ ln: lang_code }}
                    />
                  )}
                </Route>
                <Route key={`${lang_code}-pay`} path={`/${lang_code}/pay`}>
                  {() => (
                    <LanguageWrapper
                      Component={StripePortal}
                      params={{ ln: lang_code }}
                    />
                  )}
                </Route>

                <Route
                  key={`${lang_code}-mode-platform`}
                  path={`/${lang_code}/:mode/:platform`}
                >
                  {(params) => (
                    <LanguageWrapper
                      Component={HomePage}
                      params={{ ...params, ln: lang_code }}
                    />
                  )}
                </Route>
              </React.Fragment>
            ))}
            <Route path="/:mode/:platform">
              {(params) => <HomePage params={params} />}
            </Route>
          </Switch>
        </div>
        <Footer></Footer>
        </div>
        {error && <Error />}
        {message && <Message />}
      </ClerkProvider>
    </HelmetProvider>
  );
};
